import { SearchOutlined } from '@mui/icons-material'
import {
  Autocomplete,
  AutocompleteProps,
  InputAdornment,
  ListItem,
  ListItemText,
  TextField,
} from '@mui/material'
import { WorkingStepIconSvg } from 'components/Common/WorkingSteps/WorkingStepIcon'
import { useLocalizedWorkingStepLabel } from 'components/Localization/useLocalizedWorkingStepLabel'
import _, { isEqual } from 'lodash'
import React, { memo, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { WorkingStepType } from 'services/APIs/InternalAPI/internal-api.contracts'
import { markText } from 'utils/textFilterUtils'

const WorkingStepAutoComplete = memo(
  (props: {
    availableWorkingSteps: Array<WorkingStepType>
    onOptionSelected: (
      workingStepType: WorkingStepType,
      event: React.SyntheticEvent,
    ) => void
    AutoCompleteProps?: Partial<
      AutocompleteProps<WorkingStepType, false, true, false>
    >
    selectedOption: WorkingStepType | null
    label?: string
    placeholder?: string
    required?: boolean
  }) => {
    const { t } = useTranslation()
    const getLocalizedWorkingStep = useLocalizedWorkingStepLabel()
    const listboxRef = useRef<HTMLUListElement>(null)
    const scrollPositionRef = useRef<number>(0)
    const pendingRestoreRef = useRef<boolean>(false)
    const { onOptionSelected } = props

    const sortedWorkingSteps = React.useMemo(() => {
      return props.availableWorkingSteps.sort((a, b) => {
        return getLocalizedWorkingStep(a).localeCompare(
          getLocalizedWorkingStep(b),
        )
      })
    }, [getLocalizedWorkingStep, props.availableWorkingSteps])

    useEffect(() => {
      if (
        pendingRestoreRef.current &&
        listboxRef.current &&
        scrollPositionRef.current > 0
      ) {
        listboxRef.current.scrollTop = scrollPositionRef.current
        pendingRestoreRef.current = true
      }
    })

    // Handle option selection with scroll position preservation
    const handleOptionSelected = useCallback(
      (
        event: React.SyntheticEvent,
        value: WorkingStepType | null,
        reason: string,
      ) => {
        if (reason === 'selectOption' && value) {
          // Save current scroll position before triggering re-render
          if (listboxRef.current) {
            scrollPositionRef.current = listboxRef.current.scrollTop
            pendingRestoreRef.current = true
          }
          onOptionSelected(value, event)
        }
      },
      [onOptionSelected],
    )

    return (
      <Autocomplete
        value={props.selectedOption || ({} as WorkingStepType)}
        onChange={handleOptionSelected}
        openOnFocus
        autoHighlight
        options={sortedWorkingSteps || []}
        slotProps={{
          listbox: {
            ref: listboxRef,
          },
        }}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              value={
                props.selectedOption
                  ? getLocalizedWorkingStep(props.selectedOption)
                  : ''
              }
              name="workingStep"
              variant="standard"
              // autoFocus
              slotProps={{
                input: {
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined color="action" />
                    </InputAdornment>
                  ),
                },
              }}
              placeholder={
                props.placeholder || t('project:working-steps-available')
              }
              label={props.label}
              required={props.required}
            />
          )
        }}
        renderOption={(baseElProps, option, state) => {
          return (
            <ListItem
              disableGutters
              {...baseElProps}
              key={option}
              data-value={option}
            >
              <WorkingStepIconSvg
                attributes={{
                  width: '32px',
                }}
                workingStepType={option}
              />
              <ListItemText
                style={{
                  paddingLeft: '16px',
                }}
                primary={markText(
                  getLocalizedWorkingStep(option),
                  state.inputValue,
                )}
              />
            </ListItem>
          )
        }}
        getOptionLabel={(option) => {
          if (WorkingStepType[option]) {
            return getLocalizedWorkingStep(option)
          } else {
            if (_.isEmpty(option)) {
              return ''
            }
            return option
          }
        }}
        {...props.AutoCompleteProps}
      />
    )
  },
  isEqual,
)

WorkingStepAutoComplete.displayName = 'WorkingStepAutoComplete'

export default WorkingStepAutoComplete
